import { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  useTheme,
} from '@mui/material';

import { useGetContractQuery } from '@/apis/extended/contractApi';
import { VoidContractButton } from '@/components/Negotiation/VoidContractButton';
import { Product } from '@/types/ProductType';

type ProductContractSummaryProps = {
  product?: Product;
  contractId?: string;
  userRole?: 'agent' | 'seller' | 'buyer';
  contractStatus?: string;
  isRatified?: boolean;
  showRejectButton?: boolean;
  onRejectSuccess?: () => void;
};

export const ProductContractSummary = ({
  product,
  contractId,
  userRole = 'agent',
  contractStatus: propContractStatus,
  isRatified: propIsRatified,
  showRejectButton = false,
  onRejectSuccess,
}: ProductContractSummaryProps) => {
  const { data: contract } = useGetContractQuery(contractId ?? '', {
    skip: !contractId,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    pollingInterval: 0,
  });

  const theme = useTheme();

  // Use prop values or fall back to contract values
  const contractStatus = propContractStatus || contract?.contractStatus;
  const isRatified =
    propIsRatified || contract?.isRatified || contractStatus === 'ratified';
  const isVoided = contractStatus === 'voided';

  // Get property address from contract or product
  // Priority: seller overrides > product database (source of truth) > legacy agentSellerInfo > product name
  const propertyAddress =
    contract?.sellerPropertyInfo?.propertyAddress ||
    contract?.product?.address?.fullAddress ||
    contract?.product?.address?.street ||
    contract?.agentSellerInfo?.propertyAddress ||
    product?.address?.fullAddress ||
    product?.address?.street ||
    product?.name ||
    'N/A';

  // Get offer price
  const offerPrice =
    contract?.offerSpecifics?.offerPrice ||
    contract?.agentOfferInfo?.offerPrice ||
    product?.price ||
    0;

  // Get closing date
  const closingDate =
    contract?.offerSpecifics?.closingDate ||
    contract?.agentOfferInfo?.closingDate ||
    null;

  // Get financing commitment date
  const financingCommitmentDate =
    contract?.offerSpecifics?.financingCommittmentDate || null;

  // Get financing type
  const financingType =
    contract?.contingency?.financingContingency ||
    contract?.offerSpecifics?.loanProgram ||
    'N/A';

  // Get contract created date as offer date
  const offerDate = contract?.createdAt || null;

  const checkItems = useMemo(() => {
    const items = [
      {
        label: 'Contract started',
        value: Boolean(contract),
      },
      {
        label: 'Buyer submitted the contract',
        value: Boolean(contract?.buyerSign?.isStored),
      },
      {
        label: 'Contract email sent to agent',
        value: Boolean(contract?.agentEmailSent),
      },
      {
        label: 'Agent received the contract',
        value: Boolean(contract?.agentReceived),
      },
      {
        label: 'Agent confirmed details of the contract',
        value: Boolean(contract?.agentSign?.isStored),
      },
      {
        label: 'Contract email sent to seller',
        value: Boolean(contract?.sellerEmailSent),
      },
      {
        label: 'Seller received the contract',
        value: Boolean(contract?.sellerReceived),
      },
      {
        label: 'Seller submitted the contract',
        value: Boolean(contract?.sellerSign?.isStored),
      },
    ];
    return items;
  }, [contract]);

  return (
    <Card>
      <CardHeader
        title="Contract Summary"
        avatar={<i className="fa-light fa-clipboard-list-check" />}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Small Reject Button - Only for seller when showRejectButton is true */}
            {showRejectButton && userRole === 'seller' && contractId && (
              <VoidContractButton
                contractId={contractId}
                voidedBy="seller"
                contractStatus={contractStatus}
                isRatified={isRatified}
                onVoidSuccess={onRejectSuccess}
                buttonText="Reject"
                variant="outline"
                size="sm"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              />
            )}

            {/* Status Badge */}
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                borderRadius: '16px',
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: isRatified
                  ? '#22c55e'
                  : isVoided
                  ? '#dc2626'
                  : contractStatus === 'negotiating' ||
                    contractStatus === 'draft'
                  ? '#f97316'
                  : contractStatus === 'awaiting_signatures'
                  ? '#3b82f6'
                  : '#6b7280',
                color: 'white',
                textTransform: 'capitalize',
              }}
            >
              {isRatified
                ? 'Ratified'
                : isVoided
                ? 'Voided'
                : contractStatus?.replace(/_/g, ' ') || 'Draft'}
            </Box>
          </Box>
        }
      />
      <CardContent>
        {/* Property & Contract Details */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.greyShades.grey1 }}
            >
              Property Address
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {propertyAddress}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Amount
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#22c55e' }}
              >
                ${offerPrice?.toLocaleString() || '0'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Offer Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {offerDate ? new Date(offerDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Closing Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {closingDate
                  ? new Date(closingDate).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Commitment
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {financingCommitmentDate
                  ? new Date(financingCommitmentDate).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.greyShades.grey1, display: 'block' }}
              >
                Financing Type
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textTransform: 'capitalize' }}
              >
                {financingType === 'at_least_purchase_price'
                  ? 'Mortgage Financing'
                  : financingType === 'cash'
                  ? 'Cash Offer'
                  : financingType || 'N/A'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contract Progress Checklist */}
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Contract Progress
          </Typography>
          {checkItems.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                ml: 0.5,
                mt: 1,
              }}
            >
              {item.value ? (
                <i
                  className="fa-light fa-check color-success"
                  style={{ fontSize: '14px' }}
                />
              ) : (
                <i
                  className="fa-light fa-close color-error"
                  style={{ fontSize: '14px' }}
                />
              )}
              <Typography
                sx={{
                  color: theme.palette.greyShades.grey1,
                  fontSize: '0.875rem',
                }}
                variant="body2"
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};
